import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLDoctype = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Document Architecture</div>
                <h1>DOCTYPE & HTML Declaration</h1>
                <p className="hero-lead">
                    The DOCTYPE declaration is the first line of every HTML document. Understanding it is crucial for
                    proper browser rendering and standards compliance.
                </p>
            </div>

            <div className="content-section">
                <h2>What is DOCTYPE?</h2>
                <p>
                    <strong>DOCTYPE</strong> (Document Type Declaration) tells the browser which version of HTML the page uses.
                    It triggers <strong>standards mode</strong> rendering, ensuring consistent behavior across browsers.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>HTML5 DOCTYPE (Modern Standard)</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html>`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>HTML5 Simplification:</strong> Unlike previous versions, HTML5's DOCTYPE is beautifully simple.
                        No more memorizing complex DTD references!
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>DOCTYPE Through History</h2>
                <p>
                    Older HTML versions had verbose DOCTYPE declarations. Let's see how they evolved:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>HTML 4.01 Strict</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE HTML PUBLIC "-//W3C//DTD HTML 4.01//EN"
    "http://www.w3.org/TR/html4/strict.dtd">`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>HTML 4.01 Transitional</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE HTML PUBLIC "-//W3C//DTD HTML 4.01 Transitional//EN"
    "http://www.w3.org/TR/html4/loose.dtd">`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>XHTML 1.0 Strict</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Strict//EN"
    "http://www.w3.org/TR/xhtml1/DTD/xhtml1-strict.dtd">`} />
                </div>

                <div className="comparison-table">
                    <h3>Why These Were Complicated:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Component</th>
                                <th>Meaning</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>PUBLIC</code></td>
                                <td>Publicly available DTD (Document Type Definition)</td>
                            </tr>
                            <tr>
                                <td><code>-//W3C//</code></td>
                                <td>Organization (W3C in this case)</td>
                            </tr>
                            <tr>
                                <td><code>DTD HTML 4.01//EN</code></td>
                                <td>DTD name and language</td>
                            </tr>
                            <tr>
                                <td>URL</td>
                                <td>Location of DTD file (rarely used)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Good News:</strong> You don't need to memorize these anymore! HTML5's simple DOCTYPE works everywhere.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Why DOCTYPE Matters: Quirks Mode vs Standards Mode</h2>
                <p>
                    Browsers use DOCTYPE to choose between two rendering modes:
                </p>

                <div className="mode-comparison">
                    <div className="mode-card standards">
                        <h3>Standards Mode (Good)</h3>
                        <p><strong>Triggered by:</strong> Valid DOCTYPE</p>
                        <ul>
                            <li>Modern CSS and HTML work correctly</li>
                            <li>Consistent behavior across browsers</li>
                            <li>Follows W3C specifications</li>
                            <li>Predictable layout</li>
                        </ul>
                    </div>

                    <div className="mode-card quirks">
                        <h3> Quirks Mode (Bad)</h3>
                        <p><strong>Triggered by:</strong> Missing or invalid DOCTYPE</p>
                        <ul>
                            <li>Emulates old browser bugs (IE 5)</li>
                            <li>Inconsistent CSS box model</li>
                            <li>Unpredictable rendering</li>
                            <li>Modern features may not work</li>
                        </ul>
                    </div>
                </div>

                <Warning><strong> Critical:</strong> Always include <code>&lt;!DOCTYPE html&gt;</code> as the very first line.
                    Even a single space or comment before it can trigger quirks mode in some browsers!</Warning>

                <div className="code-example">
                    <div className="code-header">
                        <span className="incorrect"> Wrong (triggers quirks mode)</span>
                    </div>
                    <pre><code>{`<!-- Don't do this! --&gt;
<!DOCTYPE html>

 <!DOCTYPE html>  <!-- Space before DOCTYPE --&gt;

<html><!DOCTYPE html>  <!-- DOCTYPE not first --&gt;`}</code></pre>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span className="correct">Correct</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>Document</title>
</head>
...`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The <code>&lt;html&gt;</code> Element</h2>
                <p>
                    Immediately after DOCTYPE comes the <code>&lt;html&gt;</code> elementthe root of your document.
                    Everything else nests inside it.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>HTML Element with Language Attribute</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
    <head>
        <!-- Metadata --&gt;
    </head>
    <body>
        <!-- Content --&gt;
    </body>
</html>`} />
                </div>

                <div className="attribute-section">
                    <h3>Important <code>&lt;html&gt;</code> Attributes:</h3>

                    <h4>1. <code>lang</code> Attribute (Critical for Accessibility & SEO)</h4>
                    <div className="code-example">
                        <pre><code>{`<html lang="en">     <!-- English --&gt;
<html lang="es">     <!-- Spanish --&gt;
<html lang="fr">     <!-- French --&gt;
<html lang="de">     <!-- German --&gt;
<html lang="ja">     <!-- Japanese --&gt;
<html lang="zh-CN">  <!-- Chinese (Simplified) --&gt;
<html lang="ar">     <!-- Arabic --&gt;`}</code></pre>
                    </div>

                    <div className="benefits-list">
                        <strong>Why <code>lang</code> matters:</strong>
                        <ul>
                            <li><strong>Screen readers:</strong> Pronounce text correctly</li>
                            <li><strong>Search engines:</strong> Understand content language for targeting</li>
                            <li><strong>Browsers:</strong> Apply language-specific fonts and hyphenation</li>
                            <li><strong>Translation tools:</strong> Detect source language automatically</li>
                        </ul>
                    </div>

                    <h4>2. <code>dir</code> Attribute (Text Direction)</h4>
                    <div className="code-example">
                        <pre><code>{`<html lang="en" dir="ltr">  <!-- Left-to-right (English, most languages) --&gt;
<html lang="ar" dir="rtl">  <!-- Right-to-left (Arabic, Hebrew) --&gt;`}</code></pre>
                    </div>

                    <h4>3. <code>xmlns</code> Attribute (XHTML Legacy)</h4>
                    <div className="code-example">
                        <pre><code>{`<!-- Only needed for XHTML, not HTML5 --&gt;
<html xmlns="http://www.w3.org/1999/xhtml">

<!-- In HTML5, you can omit this --&gt;
<html lang="en">`}</code></pre>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Minimal Valid HTML5 Document</h2>
                <p>
                    While browsers are forgiving, here's the absolute minimum required for valid HTML5:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Minimal HTML5</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>Page Title</title>
</head>
<body>
    <h1>Content</h1>
</body>
</html>`} />
                </div>

                <div className="explanation-list">
                    <h3>Required Elements:</h3>
                    <ol>
                        <li><code>&lt;!DOCTYPE html&gt;</code> - Document type</li>
                        <li><code>&lt;html&gt;</code> - Root element</li>
                        <li><code>&lt;head&gt;</code> - Metadata container</li>
                        <li><code>&lt;title&gt;</code> - Document title (required by spec)</li>
                        <li><code>&lt;body&gt;</code> - Content container</li>
                    </ol>

                    <p><strong>Note:</strong> <code>&lt;meta charset="UTF-8"&gt;</code> is technically optional but strongly recommended
                        to ensure proper character encoding.</p>
                </div>
            </div>

            <div className="content-section">
                <h2>Recommended HTML5 Document Template</h2>
                <p>
                    For production use, include these additional meta tags for better compatibility and SEO:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Production-Ready Template</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <!-- Character Encoding (must be in first 1024 bytes) --&gt;
    <meta charset="UTF-8">
    
    <!-- IE Compatibility Mode (for older IE versions) --&gt;
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    
    <!-- Responsive Design --&gt;
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    
    <!-- SEO --&gt;
    <meta name="description" content="Brief page description for search engines">
    <meta name="keywords" content="keyword1, keyword2, keyword3">
    <meta name="author" content="Your Name">
    
    <!-- Page Title --&gt;
    <title>Page Title | Site Name</title>
    
    <!-- Favicon --&gt;
    <link rel="icon" href="/favicon.ico">
    
    <!-- Stylesheet --&gt;
    <link rel="stylesheet" href="styles.css">
</head>
<body>
    <!-- Main Content --&gt;
    <h1>Welcome</h1>
    <p>Your content here.</p>
    
    <!-- JavaScript (at end for performance) --&gt;
    <script src="script.js"></script>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Common DOCTYPE Mistakes</h2>
                <div className="mistakes-grid">
                    <div className="mistake-card">
                        <h3> Forgetting DOCTYPE Entirely</h3>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<html>
<head>
    <title>Page</title>
</head>`} />
                        </div>
                        <p><strong>Problem:</strong> Triggers quirks mode</p>
                        <p><strong>Fix:</strong> Add <code>&lt;!DOCTYPE html&gt;</code> as first line</p>
                    </div>


                    <div className="mistake-card">
                        <h3> Typo in DOCTYPE</h3>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!DOCTTYPE html>  <!-- Extra T --&gt;
<!DOCTYPE HTML5>  <!-- Wrong --&gt;
<!doctype html>   <!-- Actually OK! Case-insensitive --&gt;`} />
                        </div>
                        <p><strong>Problem:</strong> Typos trigger quirks mode</p>
                        <p><strong>Fix:</strong> Use exactly <code>&lt;!DOCTYPE html&gt;</code></p>
                    </div>

                    <div className="mistake-card">
                        <h3> Content Before DOCTYPE</h3>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Comment --&gt;
<!DOCTYPE html>`} />
                        </div>
                        <p><strong>Problem:</strong> Can trigger quirks mode in old IE</p>
                        <p><strong>Fix:</strong> DOCTYPE must be absolute first line</p>
                    </div>

                    <div className="mistake-card">
                        <h3> Missing <code>lang</code> Attribute</h3>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<html>  <!-- No lang attribute --&gt;`} />
                        </div>
                        <p><strong>Problem:</strong> Accessibility and SEO issues</p>
                        <p><strong>Fix:</strong> Always add <code>lang="en"</code> (or appropriate language)</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>DOCTYPE in Different Contexts</h2>

                <h3>HTML Email</h3>
                <p>Email clients have quirky rendering. Use older DOCTYPEs for better compatibility:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN"
    "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<!-- Email content --&gt;`} />
                </div>

                <h3>AMP (Accelerated Mobile Pages)</h3>
                <p>AMP requires a specific DOCTYPE and HTML tag:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html  lang="en">  <!-- Lightning bolt emoji or 'amp' attribute --&gt;
<!-- AMP-specific requirements --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Browser Developer Tools: Checking Rendering Mode</h2>
                <p>
                    You can verify which mode your page is using in browser DevTools:
                </p>

                <div className="devtools-steps">
                    <h3>Chrome/Edge DevTools:</h3>
                    <ol>
                        <li>Right-click page  Inspect</li>
                        <li>Open Console tab</li>
                        <li>Type: <code>document.compatMode</code></li>
                        <li>Press Enter</li>
                    </ol>

                    <div className="code-example">
                        <CodeBlock language="javascript" code={`> document.compatMode
"CSS1Compat"   // Standards mode (good!)
"BackCompat"   // Quirks mode (bad!)`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-list">
                    <h3>Do:</h3>
                    <ul>
                        <li>Always use <code>&lt;!DOCTYPE html&gt;</code> as the absolute first line</li>
                        <li>Include <code>lang</code> attribute on <code>&lt;html&gt;</code></li>
                        <li>Use UTF-8 character encoding</li>
                        <li>Validate your HTML at <a href="https://validator.w3.org/" target="_blank" rel="noopener noreferrer">validator.w3.org</a></li>
                    </ul>

                    <h3> Don't:</h3>
                    <ul>
                        <li>Omit DOCTYPE (triggers quirks mode)</li>
                        <li>Put anything before DOCTYPE (even comments)</li>
                        <li>Use old HTML 4.01 or XHTML DOCTYPEs for new projects</li>
                        <li>Forget the <code>lang</code> attribute (accessibility issue)</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLDoctype;



